// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import MakeenLogo from './MakeenLogo';

const Header = ({ language, changeLanguage, t }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isRTL = language === 'ar';

  console.log('Header component render - language:', language);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: '#home', label: t?.nav?.home || 'Home' },
    { href: '#about', label: t?.nav?.about || 'About' },
    { href: '#services', label: t?.nav?.services || 'Services' },
    { href: '#contact', label: t?.nav?.contact || 'Contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-lg border-b border-[#2beaf4]/20 shadow-lg shadow-[#582f8f]/10 transform translate-y-0' 
        : 'bg-transparent transform translate-y-0'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo Component */}
          <MakeenLogo language={language} variant="header" />

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                className="text-[#1b0976] hover:text-[#2beaf4] transition-all duration-300 font-medium relative group py-2"
                style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#2beaf4] via-[#582f8f] to-[#1b0976] transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </nav>

          {/* Language Toggle & CTA */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                const newLang = language === 'en' ? 'ar' : 'en';
                console.log('Language button clicked in Header, changing from', language, 'to:', newLang);
                changeLanguage(newLang);
              }}
              className="px-3 py-1 text-sm text-[#582f8f] hover:text-[#1b0976] transition-all duration-300 rounded-md hover:bg-[#2beaf4]/10 border border-[#2beaf4]/20"
              style={{ fontFamily: language === 'ar' ? 'GE SS Two, sans-serif' : 'Poppins, sans-serif', fontWeight: '500' }}
            >
              {language === 'en' ? 'العربية' : 'English'}
            </button>
            <button 
              className="hidden md:inline-flex items-center px-6 py-2 bg-gradient-to-r from-[#2beaf4] via-[#582f8f] to-[#1b0976] text-white rounded-lg hover:from-[#1b0976] hover:via-[#582f8f] hover:to-[#2beaf4] transition-all duration-300 font-medium shadow-lg shadow-[#2beaf4]/25 hover:shadow-xl hover:shadow-[#582f8f]/30 transform hover:-translate-y-0.5 hover:scale-105"
              style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}
            >
              {t?.nav?.getStarted || 'Get Started'}
            </button>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-[#582f8f] hover:text-[#1b0976] transition-colors duration-300 rounded-lg hover:bg-[#2beaf4]/10"
            >
              <div className="relative w-6 h-6">
                <span className={`absolute top-1/2 left-1/2 w-5 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? 'rotate-45 transform -translate-x-1/2 -translate-y-1/2' : 'rotate-0 transform -translate-x-1/2 -translate-y-2'
                }`}></span>
                <span className={`absolute top-1/2 left-1/2 w-5 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? 'opacity-0' : 'opacity-100 transform -translate-x-1/2 -translate-y-1/2'
                }`}></span>
                <span className={`absolute top-1/2 left-1/2 w-5 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? '-rotate-45 transform -translate-x-1/2 -translate-y-1/2' : 'rotate-0 transform -translate-x-1/2 translate-y-1'
                }`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-500 ease-out ${
          isMenuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="py-4 border-t border-[#2beaf4]/20 bg-white/95 backdrop-blur-lg">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className="text-[#1b0976] hover:text-[#2beaf4] transition-all duration-300 font-medium py-2 px-4 rounded-lg hover:bg-[#2beaf4]/10 transform hover:translate-x-2"
                  style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <button 
                className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-[#2beaf4] via-[#582f8f] to-[#1b0976] text-white rounded-lg hover:from-[#1b0976] hover:via-[#582f8f] hover:to-[#2beaf4] transition-all duration-300 font-medium shadow-lg shadow-[#2beaf4]/25 hover:shadow-xl hover:shadow-[#582f8f]/30 transform hover:-translate-y-0.5"
                style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}
                onClick={() => setIsMenuOpen(false)}
              >
                {t?.nav?.getStarted || 'Get Started'}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;